import { Component } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { AppConfigService } from '@markant-platform/shared/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { catchError, filter, map, Observable, of, startWith, switchMap } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import {
  PLATFORM_ROLES_WITH_ADMIN_RIGHTS,
  PLATFORM_ROLES_WITHOUT_DEMO_USER,
} from '@markant-platform/shared/user/model';
import { getAdminRouteByRole } from '@markant-platform/backoffice/data-access';

export type RouteConfig = {
  key: string;
  label: string;
  route?: string;
  external?: boolean;
  roles?: string[];
};

@Component({
  selector: 'markant-platform-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [
    trigger('showHideMenu', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('150ms', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [animate('150ms', style({ transform: 'translateX(100%)' }))]),
    ]),
  ],
})
export class HeaderComponent {
  activeTabIndex$!: Observable<number>;
  visibleRoutes$: Observable<RouteConfig[]>;

  readonly MARKANT_LOGO_PATH = 'assets/images/logo/markant-logo.svg';

  private readonly tabDefinitions: RouteConfig[] = [
    {
      key: 'dashboard',
      label: 'header.navigation.dashboard',
      route: '/',
      external: false,
    },
    {
      key: 'cartridges',
      label: 'header.navigation.cartridges',
      route: '/cartridge/catalog',
      external: false,
    },
    {
      key: 'news',
      label: 'header.navigation.news',
      route: '/news',
      external: false,
    },
    {
      key: 'support',
      label: 'header.navigation.support',
      external: true,
      roles: PLATFORM_ROLES_WITHOUT_DEMO_USER,
    },
    {
      key: 'backoffice',
      label: 'header.navigation.admin',
      external: false,
      roles: PLATFORM_ROLES_WITH_ADMIN_RIGHTS,
    },
  ];

  constructor(
    private readonly router: Router,
    private readonly authService: AuthenticatedUserService,
    private readonly translocoService: TranslocoService
  ) {
    this.visibleRoutes$ = this.getVisibleRoutes$();
    this.activeTabIndex$ = this.getActiveTabIndex$();
  }

  private getVisibleRoutes$(): Observable<RouteConfig[]> {
    return this.authService.getUserRoles().pipe(
      map(roles => roles.map(({ name }) => name)),
      catchError(() => {
        return of([]);
      }),
      switchMap(roles =>
        this.translocoService.langChanges$.pipe(
          map(lang => this.getVisibleRoutes(this.tabDefinitions, lang.substring(0, 2), roles))
        )
      )
    );
  }

  private getVisibleRoutes(routes: RouteConfig[], lang: string, roles: string[]): RouteConfig[] {
    return routes
      .filter(tab => !!tab.route || !tab.roles || roles.some(role => tab.roles?.includes(role)))
      .map(tab => {
        if (tab.key === 'backoffice') {
          tab.route = getAdminRouteByRole(roles)?.at(0);
        }

        if (tab.key === 'support') {
          const supportUrl = AppConfigService.appConfiguration.supportPortalUrl;
          tab.route = supportUrl.replace(/{{lang}}/g, lang);
        }

        return tab;
      });
  }

  private getActiveTabIndex$(): Observable<number> {
    return this.router.events.pipe(
      startWith(new NavigationEnd(0, this.router.url, this.router.url)),
      filter(event => event instanceof NavigationEnd),
      map(event => event as NavigationEnd),
      switchMap((navigationEndEvent: NavigationEnd) =>
        this.findMatchingIndexByRouterUrl(navigationEndEvent.urlAfterRedirects)
      ),
      map(index => (index === -1 ? this.tabDefinitions.length : index))
    );
  }

  private findMatchingIndexByRouterUrl(routerUrl: string): Observable<number> {
    return this.visibleRoutes$.pipe(
      map(tabs => {
        return tabs
          .map(({ route }) => route)
          .findIndex(route => {
            if (!route) {
              return false;
            }
            return route === '/' ? routerUrl === route : routerUrl.startsWith(route);
          });
      })
    );
  }
}
